import { createApi } from '@reduxjs/toolkit/query/react';
import { getHeaders } from 'auth/auth';
import { baseQueryWithReauth } from '../baseQuery';

// RTK Query service for the POS Rate Exception (Discretion) feature (PROD-332).
// Backend: /api/v1/b2b/loan/applications/{loan_id}/discretion/*
//
// LOS contract is live (probe-verified 2026-07-14): submit pushes the
// approval request into LOS, GET reconciles RVP decisions back on every
// fetch (poll fallback), and /approvers proxies the LOS user list. A
// missing `los_approval_request_id` on a pending row means the LOS push
// degraded — the UI shows the "LOS sync pending" pill.
// See .claude/docs/rate_exception_implementation_plan.md §14.

export const discretionAPI = createApi({
  reducerPath: 'discretion',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['discretion'],
  endpoints: (builder) => ({
    // List all discretion requests on an application + Submit Loan gate flag.
    getDiscretionRequests: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/applications/${applicationId}/discretion`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, { applicationId }) => [
        { type: 'discretion', id: applicationId },
      ],
    }),

    // Submit a fresh discretion request for a rate row.
    submitDiscretionRequest: builder.mutation({
      query: ({
        applicationId,
        rateRowKey,
        discretionValue,
        requestedApproverId,
        requestedApproverName,
        comment,
      }) => ({
        url: `/b2b/loan/applications/${applicationId}/discretion`,
        method: 'POST',
        body: {
          rate_row_key: rateRowKey,
          discretion_value: discretionValue,
          requested_approver_id: requestedApproverId,
          requested_approver_name: requestedApproverName,
          comment,
        },
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'discretion', id: applicationId },
      ],
    }),

    // Cancel a pending discretion request.
    cancelDiscretionRequest: builder.mutation({
      query: ({ applicationId, requestId, cancellationNote }) => ({
        url: `/b2b/loan/applications/${applicationId}/discretion/${requestId}/cancel`,
        method: 'POST',
        body: { cancellation_note: cancellationNote },
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'discretion', id: applicationId },
      ],
    }),

    // Approver (RVP) dropdown data — LOS users filtered by the RVP role
    // config on the backend. Not app-specific; keyed under the loan route
    // for RBAC consistency.
    getDiscretionApprovers: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/applications/${applicationId}/discretion/approvers`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    // Dev-only: simulate an LOS state change. Hidden from public flow;
    // exists so the end-to-end state-mirror path can be exercised
    // manually before the LOS webhook contract is wired.
    syncDiscretionFromLos: builder.mutation({
      query: ({
        applicationId,
        losApprovalRequestId,
        status,
        decisionComment,
        decidedByName,
      }) => ({
        url: `/b2b/loan/applications/${applicationId}/discretion/sync`,
        method: 'POST',
        body: {
          los_approval_request_id: losApprovalRequestId,
          status,
          decision_comment: decisionComment,
          decided_by_name: decidedByName,
        },
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'discretion', id: applicationId },
      ],
    }),
  }),
});

export const {
  useGetDiscretionRequestsQuery,
  useGetDiscretionApproversQuery,
  useSubmitDiscretionRequestMutation,
  useCancelDiscretionRequestMutation,
  useSyncDiscretionFromLosMutation,
} = discretionAPI;
